/**
 * Live cursors.
 *
 * ## Coordinates are content-relative, not viewport-relative
 *
 * Each cursor position is normalised 0..1 against the *board content* - the full
 * scrollable surface - rather than against the visible viewport. The overlay is
 * absolutely positioned inside the content element, so a peer's cursor stays
 * pinned to the same card no matter how either user has scrolled.
 *
 * Viewport-relative coordinates would be simpler and wrong: two users looking at
 * different columns would each see a cursor floating over whatever happened to be
 * at that screen position, which is worse than showing nothing, because it looks
 * like information.
 *
 * ## Why a CSS transition rather than interpolating in JavaScript
 *
 * Frames arrive at 20 Hz (see `CURSOR_THROTTLE_MS`). Twenty discrete positions a
 * second reads as a slight stutter; a short linear transition between them reads
 * as continuous motion, and it costs one CSS declaration instead of an animation
 * loop with its own frame budget. The transition is 90 ms - slightly longer than
 * the 50 ms interval - so a frame that arrives late still has time to glide
 * rather than jump, and short enough that a cursor which stops does not visibly
 * coast past its final position.
 *
 * Under `prefers-reduced-motion` the global rule in `styles.css` collapses the
 * transition, and the cursors step. That is the correct behaviour: the user asked
 * for less motion, and a stepping cursor is still readable.
 *
 * ## Why the overlay is `aria-hidden`
 *
 * A remote cursor is a visual affordance with no textual content of its own. The
 * name beside it is a label for a shape, not a document element. Announcing it
 * would mean a screen reader user hears "Priya" repeatedly as a pointer moves,
 * which is noise. The presence stack is the accessible representation of the same
 * fact, and it is a proper list.
 */

import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { CURSOR_THROTTLE_MS } from '@slate/shared';
import type { RemoteCursor } from '../state/types.js';
import { otherPresence } from '../state/board-reducer.js';
import { useBoard } from '../state/BoardProvider.js';
import { readableInkOn } from '../lib/format.js';

/** A cursor older than this is not drawn. The server already reports a stale
 * cursor as absent after a minute; this is the shorter, client-side version, so a
 * pointer that stops moving fades rather than sitting frozen mid-board. */
const CURSOR_IDLE_MS = 8_000;

function toRemoteCursors(presences: ReturnType<typeof otherPresence>, now: number): RemoteCursor[] {
  const cursors: RemoteCursor[] = [];
  for (const presence of presences) {
    if (presence.cursor === null) continue;
    if (now - presence.lastSeenAt > CURSOR_IDLE_MS) continue;
    cursors.push({
      clientId: presence.clientId,
      userId: presence.userId,
      name: presence.name,
      colour: presence.colour,
      position: presence.cursor,
    });
  }
  return cursors;
}

export function LiveCursors({ now }: { now: number }): ReactNode {
  const { state } = useBoard();
  const cursors = toRemoteCursors(otherPresence(state), now);

  if (cursors.length === 0) return null;

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20">
      {cursors.map((cursor) => (
        <Cursor key={cursor.clientId} cursor={cursor} />
      ))}
    </div>
  );
}

function Cursor({ cursor }: { cursor: RemoteCursor }): ReactNode {
  return (
    <div
      className="absolute will-change-[left,top]"
      style={{
        // Percentage of the content element, which is what makes the cursor stay
        // pinned to the same card regardless of either user's scroll position.
        // A `translate3d` with percentage values would be a percentage of this
        // element's own 18px box, which is why the offset is `left`/`top` and the
        // transition is declared on those properties rather than on `transform`.
        left: `${cursor.position.x * 100}%`,
        top: `${cursor.position.y * 100}%`,
        transition: `left ${CURSOR_THROTTLE_MS + 40}ms linear, top ${CURSOR_THROTTLE_MS + 40}ms linear`,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" className="drop-shadow-sm">
        <path d="M2 1.5 15.5 8 9 9.5 6.5 16z" fill={cursor.colour} stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
      <span
        className="mt-0.5 ml-3 inline-block max-w-[10rem] truncate rounded px-1.5 py-0.5 text-[11px] font-medium"
        style={{ backgroundColor: cursor.colour, color: readableInkOn(cursor.colour) }}
      >
        {cursor.name}
      </span>
    </div>
  );
}

/**
 * Report this user's pointer position.
 *
 * Attached to the board content element rather than to `window` so that a
 * pointer over the sidebar or the drawer is not reported as a board position -
 * which would draw this user's cursor in a corner of the board for everyone else
 * while they are reading a comment.
 *
 * The throttle is inside `BoardSocket.sendCursor`, which coalesces to 20 Hz and
 * flushes on the trailing edge. Calling this on every pointer move is therefore
 * correct and cheap: the coalescer holds the latest value and sends one frame per
 * window.
 */
export function useCursorReporter(contentRef: RefObject<HTMLElement | null>): {
  onPointerMove(event: ReactPointerEvent<HTMLElement>): void;
  onPointerLeave(): void;
} {
  const { actions } = useBoard();
  const lastRef = useRef(0);

  // Flush on unmount so a pointer that leaves mid-window does not strand a peer's
  // cursor a few pixels behind the truth.
  useEffect(() => {
    return () => actions.flushCursor();
  }, [actions]);

  return {
    onPointerMove(event) {
      const element = contentRef.current;
      if (element === null) return;

      // `pointerType` filtering keeps a touch drag from emitting a cursor: a
      // finger is not a pointer, and a cursor that appears wherever someone
      // tapped and then vanishes is noise for everyone else.
      if (event.pointerType === 'touch') return;

      const now = performance.now();
      // A cheap gate in front of the coalescer. The coalescer would handle this
      // correctly on its own; skipping the rect read for frames that cannot
      // possibly be sent avoids a layout-forcing `getBoundingClientRect` on every
      // pointer event, which is the expensive part.
      if (now - lastRef.current < CURSOR_THROTTLE_MS / 2) return;
      lastRef.current = now;

      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      actions.sendCursor(
        Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
      );
    },
    onPointerLeave() {
      actions.flushCursor();
    },
  };
}
